import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react-v3/polaris/space-between";
import AppLayout from "@amzn/awsui-components-react-v3/polaris/app-layout";
import Form from "@amzn/awsui-components-react-v3/polaris/form";
import Grid from "@amzn/awsui-components-react-v3/polaris/grid";
import Flashbar from "@amzn/awsui-components-react-v3/polaris/flashbar";
import { SubscriptionDetailsFormContainer } from "../../components/SubscriptionDetailsFormContainer/SubscriptionDetailsFormContainer";
import { BillingDetailsFormContainer } from "../../components/BillingDetailsFormContainer/BillingDetailsFormContainer";
import { SubscriptionPaymentDetailsContainer } from "../../components/SubscriptionPaymentDetailsContainer/SubscriptionPaymentDetailsContainer";
import { SubscriptionSummaryContainer } from "../../components/SubscriptionSummaryContainer/SubscriptionSummaryContainer";
import { createContext, useContext, useEffect, useState } from "react";
import SBTSBreadcrumbGroup from "../../components/BreadcrumbGroup/BreadcrumbGroup";
import { useNavigate } from "react-router-dom";
import { Button, ColumnLayout } from "@amzn/awsui-components-react-v3";
import { ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT } from "../../common/routes";
import { generateAwsSignInUrl, verifyJwt } from "../../auth/awsSignIn";
import LoadingContent from "../../components/LoadingContent/LoadingContent";
import { useFinalizeTeamSubscriptionMutation } from "../../user/api/mutations";
import { useGetOfferPriceForAccount } from "../../user/api/queries";
import { UserContext } from "../../App";
import { isAutoRenewalEnabled } from "../../common/constants";
import { AwsAccountType, COMPANY_NAME_REGEX, EMAIL_REGEX, MINIMUM_SBTS_SEATS_PER_REQUEST_CUSTOMER, MINIMUM_SBTS_SEATS_PER_REQUEST_PARTNER, NAME_REGEX, OPPORTUNITY_REGEX, SessionKey, SignInMethod } from "../../common/constants";
import { isPartnerAccount, matchRegex } from "../../common/utilityFunctions";
import { AccountPlanType } from "../../user/types";
import { getRegionFromCountryName } from "../../common/constants";
import { triggerAnalyticsEvent } from "../../analytics/createEventTrigger";
import { getPartnerPricePerSeat } from "../../common/constants";
import { getNodeEnvironment } from "../../common/nodeEnvironment";
export const ConfigureSubscriptionContext = createContext({ numberSeats: 40 });
export const ConfigureSubscriptionPage = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const user = useContext(UserContext);
    const [isJwtValid, setIsJwtTokenValid] = useState(false);
    const [awsJwtToken, setAwsJwtToken] = useState();
    const [awsAccountId, setAwsAccountId] = useState();
    const [otpEmailDomain, setEmailDomain] = useState();
    const [awsAccountType, setAwsAccountType] = useState();
    const [discountPercentage, setDiscountPercentage] = useState();
    const [formSubmitError, setFormSubmitError] = useState();
    const { finalizeTeamSubscription, finalizeTeamSubscriptionLoading, finalizeTeamSubscriptionError } = useFinalizeTeamSubscriptionMutation();
    const { data: offerPriceData, errors: offerPriceDataErrorList, error: offerPriceDataError, loading: offerPriceDataLoading } = useGetOfferPriceForAccount(sessionStorage.getItem(SessionKey.AWS_ACCOUNT_ID), user?.id, user?.email, ((sessionStorage.getItem(SessionKey.IS_AMAZON_TRAINING_PARTNER)) ? true : false), user?.vibeId);
    const isFTv2Account = offerPriceData?.getPayerAccountOfferPrice?.accountPlanType === AccountPlanType.FREE;
    const defaultFieldData = {
        value: '',
        error: ''
    };
    const [formData, setFormData] = useState({
        firstName: defaultFieldData,
        lastName: defaultFieldData,
        businessEmail: defaultFieldData,
        companyName: defaultFieldData,
        distributionSellerName: defaultFieldData,
        endCustomerCompanyName: defaultFieldData,
        endCustomerCountry: defaultFieldData,
        signInMethod: defaultFieldData,
        tcFirstName: defaultFieldData,
        tcLastName: defaultFieldData,
        tcEmail: defaultFieldData,
        preferredStartDate: defaultFieldData,
        numberOfSeats: defaultFieldData,
        // orderTotal: '',
        learningAdministrators: [{
                laFirstName: defaultFieldData,
                laLastName: defaultFieldData,
                laEmail: defaultFieldData
            }],
        learningAdministratorRequiredError: '',
        geo: defaultFieldData,
        aceOpportunityId: defaultFieldData,
        allowedEmailDomains: defaultFieldData,
        language: '',
        autoRenewal: true,
        agreementChecked: true,
    });
    const verifyJwtToken = (user, awsAccountJwt, setIsJwtTokenValid) => {
        verifyJwt(awsAccountJwt)
            .then(() => {
            try {
                setIsJwtTokenValid(true);
            }
            catch (err) {
                console.error(err);
                throw err;
            }
        })
            .catch(() => {
            setIsJwtTokenValid(false);
            window.location.assign(generateAwsSignInUrl({ userId: user.id }));
        });
    };
    //NOTE: I don't know if this effect is ever run
    useEffect(() => {
        // Get AWS Sign In JWT token from somewhere
        // Extract and set the AWS Account Id
        const awsAccountId = sessionStorage.getItem(SessionKey.AWS_ACCOUNT_ID);
        const awsAccountJwt = sessionStorage.getItem(SessionKey.AWS_ACCOUNT_JWT);
        const awsAccountType = sessionStorage.getItem(SessionKey.AWS_ACCOUNT_TYPE);
        const otpEmail = sessionStorage.getItem(SessionKey.OTP_EMAIL);
        const otpDomain = otpEmail?.split('@')[1];
        if (awsAccountId && awsAccountJwt && awsAccountType) {
            setAwsAccountId(awsAccountId);
            setAwsJwtToken(awsAccountJwt);
            setAwsAccountType(awsAccountType);
            setEmailDomain(otpDomain);
            verifyJwtToken(user, awsAccountJwt, setIsJwtTokenValid);
            triggerAnalyticsEvent("click", `SBTS_${(getNodeEnvironment())} | configure_subscription_management | form_display | event_succeeded`);
        }
        else {
            triggerAnalyticsEvent("click", `SBTS_${(getNodeEnvironment())} | configure_subscription_management | form_display | event_failed`);
            window.location.assign(generateAwsSignInUrl({ userId: user.id }));
        }
    }, []);
    /**
     * Validates that the user added at least 1 learning admin row.
     * It does not validate the values inside the LA firstname, lastname and email fields.
     * @returns true if user added at least 1 learning admin row
     */
    const handleLearningAdministratorListUpdate = () => {
        let isValid = true;
        const learningAdministrators = formData.learningAdministrators;
        const learningAdministratorRequiredError = !learningAdministrators?.length ? t('subscription_form_learning_admin_count_error') : '';
        setFormData(prev => {
            return {
                ...prev,
                learningAdministratorRequiredError
            };
        });
        if (learningAdministratorRequiredError) {
            isValid = false;
        }
        return isValid;
    };
    /**
     * Constructs an event object. Useful when user tries to submit form without touching a field.
     */
    const constructEventObject = (value) => {
        return {
            detail: {
                value
            }
        };
    };
    /**
     * Calls validation function for each form field
     * @returns true if form validation is successful, false otherwise
     */
    const validateForm = () => {
        let isFormValid = true;
        // Common field validation
        const firstNameValid = handleFormDataChange.firstName(constructEventObject(formData.firstName.value));
        const lastNameValid = handleFormDataChange.lastName(constructEventObject(formData.lastName.value));
        const businessEmailValid = handleFormDataChange.businessEmail(constructEventObject(formData.businessEmail.value));
        const companyNameValid = handleFormDataChange.companyName(constructEventObject(formData.companyName.value));
        const signInMethodValid = handleFormDataChange.signInMethod(constructEventObject(formData.signInMethod.value));
        const numberOfSeatsValid = handleFormDataChange.numberOfSeats(constructEventObject(formData.numberOfSeats.value));
        const preferredStartDateValid = handleFormDataChange.preferredStartDate(constructEventObject(formData.preferredStartDate.value));
        const regionValid = handleFormDataChange.region({ detail: { selectedOption: { value: awsAccountType === AwsAccountType.PARTNER ? formData.endCustomerCountry.value : formData.geo.value } } });
        // Only validate email domains for SSO and when user provides email domains for other than SSO
        const allowedEmailDomainsValid = formData.signInMethod.value === SignInMethod.SSO || formData.allowedEmailDomains.value ?
            handleFormDataChange.allowedEmailDomains(constructEventObject(formData.allowedEmailDomains.value)) :
            true;
        const learningAdministratorListSizeValid = handleLearningAdministratorListUpdate();
        isFormValid = firstNameValid
            && lastNameValid
            && businessEmailValid
            && companyNameValid
            && numberOfSeatsValid
            && signInMethodValid
            && preferredStartDateValid
            && regionValid
            && learningAdministratorListSizeValid
            && allowedEmailDomainsValid;
        // Learning admin row added. Validate the values inside the fields
        if (learningAdministratorListSizeValid) {
            const laRowValidationResult = formData.learningAdministrators.map((la, ind) => {
                const laFirstNameValid = handleFormDataChange.laFirstName(constructEventObject(la.laFirstName.value), ind);
                const laLastNameValid = handleFormDataChange.laLastName(constructEventObject(la.laLastName.value), ind);
                const laEmailValid = handleFormDataChange.laEmail(constructEventObject(la.laEmail.value), ind);
                return laFirstNameValid && laLastNameValid && laEmailValid;
            });
            isFormValid = isFormValid && laRowValidationResult.reduce((acc, curr) => acc && curr, true);
        }
        // SSO specific validations for both ATP and regular customers
        if (formData.signInMethod.value === SignInMethod.SSO) {
            const tcDetailsEntered = formData.tcFirstName.value || formData.tcLastName.value || formData.tcEmail.value;
            const tcFirstNameValid = tcDetailsEntered ? handleFormDataChange.tcFirstName(constructEventObject(formData.tcFirstName.value)) : true;
            const tcLastNameValid = tcDetailsEntered ? handleFormDataChange.tcLastName(constructEventObject(formData.tcLastName.value)) : true;
            const tcEmailValid = tcDetailsEntered ? handleFormDataChange.tcEmail(constructEventObject(formData.tcEmail.value)) : true;
            isFormValid = isFormValid && tcFirstNameValid && tcLastNameValid && tcEmailValid;
        }
        // ATP specific validations
        if (isPartnerAccount(awsAccountType)) {
            const endCustomerCompanyNameValid = handleFormDataChange.endCustomerCompanyName(constructEventObject(formData.endCustomerCompanyName.value));
            const endCustomerCountryValid = handleFormDataChange.endCustomerCountry({ detail: { selectedOption: { value: formData.endCustomerCountry.value } } });
            const aceOpportunityIdValid = handleFormDataChange.aceOpportunityId(constructEventObject(formData.aceOpportunityId.value));
            const distributionSellerNameValid = handleFormDataChange.distributionSellerName(constructEventObject(formData.distributionSellerName.value));
            isFormValid = isFormValid && endCustomerCompanyNameValid && endCustomerCountryValid && aceOpportunityIdValid && distributionSellerNameValid;
        }
        if (!isPartnerAccount(awsAccountType) && isAutoRenewalEnabled() && formData.autoRenewal && !formData.agreementChecked) {
            isFormValid = false;
        }
        return isFormValid;
    };
    /********** Validation functions for each form field **********/
    const validateFirstName = (value) => {
        if (!value) {
            return t("subscription_form_first_name_required_error_text");
        }
        if (!matchRegex(value, NAME_REGEX)) {
            return t("subscription_form_name_alphabets_error");
        }
        return '';
    };
    const validateLastName = (value) => {
        if (!value) {
            return t("subscription_form_last_name_required_error_text");
        }
        if (!matchRegex(value, NAME_REGEX)) {
            return t("subscription_form_name_alphabets_error");
        }
        return '';
    };
    const validateBusinessEmail = (value) => {
        if (!value) {
            return t("subscription_form_business_email_required_error_text");
        }
        if (value && !matchRegex(value, EMAIL_REGEX)) {
            return t("subscription_form_invalid_email_error_text");
        }
        const businessEmailDomain = value?.split('@')[1];
        if (otpEmailDomain !== businessEmailDomain) {
            return t("subscription_form_business_email_domain_error_text");
        }
        return '';
    };
    const validateCompanyName = (value) => {
        if (!value) {
            return t("subscription_form_company_name_required_error_text");
        }
        if (!matchRegex(value, COMPANY_NAME_REGEX)) {
            return t("subscription_form_company_name_error");
        }
        return '';
    };
    const validateDistributorName = (value) => {
        if (value && !matchRegex(value, COMPANY_NAME_REGEX)) {
            return t("subscription_form_distributor_name_error");
        }
        return '';
    };
    const validateNumberOfSeats = (value) => {
        if (!value) {
            return t("subscription_form_number_of_seats_required_error_text");
        }
        if (value && Number.isNaN(value)) {
            return t('subscription_form_number_of_seats_should_be_number_error_text');
        }
        if (value && Number.isInteger(value)) {
            return t('subscription_form_number_of_seats_should_be_integer_error_text');
        }
        const minSeats = isPartnerAccount(awsAccountType)
            ? MINIMUM_SBTS_SEATS_PER_REQUEST_PARTNER
            : MINIMUM_SBTS_SEATS_PER_REQUEST_CUSTOMER;
        if (value && +value < minSeats) {
            return t('subscription_form_minimum_number_of_seats_error_text', { minSeats });
        }
        if (value && +value >= 10000) {
            return t('subscription_form_number_of_seats_should_be_at_less_than_10000_error_text');
        }
        return '';
    };
    const validateSignInMethod = (value) => {
        if (!value) {
            return t("subscription_form_sign_in_method_required_error_text");
        }
        return '';
    };
    const validateEmail = (value) => {
        if (!value) {
            return t('subscription_form_email_required_error_text');
        }
        if (value && !matchRegex(value, EMAIL_REGEX)) {
            return t("subscription_form_invalid_email_error_text");
        }
        if (awsAccountType !== AwsAccountType.PARTNER) {
            if (otpEmailDomain !== value.split('@')[1]) {
                return t("subscription_form_business_email_description");
            }
        }
        return '';
    };
    const validateEndCustomerCountry = (value) => {
        if (!value) {
            return t("subscription_form_end_customer_country_required_error_text");
        }
        return '';
    };
    const validateEndCustomerCompanyName = (value) => {
        if (!value) {
            return t("subscription_form_end_customer_country_required_error_text");
        }
        if (!matchRegex(value, COMPANY_NAME_REGEX)) {
            return t("subscription_form_company_name_error");
        }
        return '';
    };
    const validateRegion = (value) => {
        if (awsAccountType !== AwsAccountType.PARTNER) {
            value = getRegionFromCountryName(value);
        }
        if (!value) {
            return t('subscription_form_region_required_error_text');
        }
        return '';
    };
    const validateAceOpportunityId = (value) => {
        if (!value && isPartnerAccount(awsAccountType)) {
            return t("subscription_form_ace_opportunity_id_required_error_text");
        }
        if (value && !OPPORTUNITY_REGEX.test(value)) {
            return t("subscription_form_ace_opportunity_id_invalid_error_text");
        }
        return '';
    };
    const validateAllowedEmailDomains = (value) => {
        if (formData.signInMethod.value == SignInMethod.SSO || value) {
            if (!value) {
                return t("subscription_form_allowed_email_domains_required_error_text");
            }
            const domains = value.split(',').map(domain => domain.trim()).filter(domain => domain); // Filter out empty strings
            if (domains.length === 0) {
                return t("subscription_form_allowed_email_domains_required_error_text");
            }
            const validDomainRegex = /^@[A-Za-z0-9][A-Za-z0-9-]{0,60}[A-Za-z0-9](\.[A-Za-z]{2,})+$/;
            const invalidDomains = domains.filter(domain => !validDomainRegex.test(domain));
            if (invalidDomains.length > 0) {
                return `${t("subscription_form_allowed_email_domains_invalid_error_text")} ${invalidDomains.join(', ')}`;
            }
            return '';
        }
    };
    /**
     * Handler function for each form field.
     * The handler calls validation function and sets the field value and error message in form data
     */
    const handleFormDataChange = {
        firstName: (e) => {
            const value = e.detail.value;
            const error = validateFirstName(value);
            setFormData(prev => {
                return {
                    ...prev,
                    firstName: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        lastName: (e) => {
            const value = e.detail.value;
            const error = validateLastName(value);
            setFormData(prev => {
                return {
                    ...prev,
                    lastName: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        businessEmail: (e) => {
            const value = e.detail.value;
            const error = validateBusinessEmail(value);
            setFormData(prev => {
                return {
                    ...prev,
                    businessEmail: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        companyName: (e) => {
            const value = e.detail.value;
            const error = validateCompanyName(value);
            setFormData(prev => {
                return {
                    ...prev,
                    companyName: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        distributionSellerName: (e) => {
            const value = e.detail.value;
            const error = validateDistributorName(value);
            setFormData(prev => {
                return {
                    ...prev,
                    distributionSellerName: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        numberOfSeats: (e) => {
            const value = e.detail.value;
            const error = validateNumberOfSeats(value);
            setFormData(prev => {
                return {
                    ...prev,
                    numberOfSeats: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        signInMethod: (e) => {
            const value = e.detail.value;
            const error = validateSignInMethod(value);
            setFormData(prev => {
                return {
                    ...prev,
                    preferredStartDate: {
                        value: '',
                        error: ''
                    },
                    signInMethod: {
                        value,
                        error
                    },
                };
            });
            return !error;
        },
        preferredStartDate: (e) => {
            const value = e.detail.value;
            const error = '';
            setFormData(prev => {
                return {
                    ...prev,
                    preferredStartDate: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        tcFirstName: (e) => {
            const value = e.detail.value;
            const error = validateFirstName(value);
            setFormData(prev => {
                return {
                    ...prev,
                    tcFirstName: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        tcLastName: (e) => {
            const value = e.detail.value;
            const error = validateLastName(value);
            setFormData(prev => {
                return {
                    ...prev,
                    tcLastName: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        tcEmail: (e) => {
            const value = e.detail.value;
            const error = validateEmail(value);
            setFormData(prev => {
                return {
                    ...prev,
                    tcEmail: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        laFirstName: (e, ind) => {
            const value = e.detail.value;
            const error = validateFirstName(value);
            const learningAdministrators = formData.learningAdministrators;
            learningAdministrators[ind].laFirstName = {
                value,
                error
            };
            setFormData(prev => {
                return {
                    ...prev,
                    learningAdministrators
                };
            });
            return !error;
        },
        laLastName: (e, ind) => {
            const value = e.detail.value;
            const error = validateLastName(value);
            const learningAdministrators = formData.learningAdministrators;
            learningAdministrators[ind].laLastName = {
                value,
                error
            };
            setFormData(prev => {
                return {
                    ...prev,
                    learningAdministrators
                };
            });
            return !error;
        },
        laEmail: (e, ind) => {
            const value = e.detail.value;
            const error = validateEmail(value);
            const learningAdministrators = formData.learningAdministrators;
            learningAdministrators[ind].laEmail = {
                value,
                error
            };
            setFormData(prev => {
                return {
                    ...prev,
                    learningAdministrators
                };
            });
            return !error;
        },
        endCustomerCompanyName: (e) => {
            const value = e.detail.value;
            const error = validateEndCustomerCompanyName(value);
            setFormData(prev => {
                return {
                    ...prev,
                    endCustomerCompanyName: {
                        value,
                        error
                    }
                };
            });
            return !error;
        },
        endCustomerCountry: (e) => {
            const value = e.detail.selectedOption.value;
            const error = validateEndCustomerCountry(value);
            setFormData(prev => {
                return {
                    ...prev,
                    endCustomerCountry: {
                        value: value,
                        error: error
                    }
                };
            });
            return !error;
        },
        region: (e) => {
            const value = e.detail.selectedOption.value;
            const error = validateRegion(value);
            setFormData(prev => {
                return {
                    ...prev,
                    geo: {
                        value: value,
                        error: error
                    }
                };
            });
            return !error;
        },
        aceOpportunityId: (e) => {
            const value = e.detail.value;
            const error = validateAceOpportunityId(value);
            setFormData(prev => ({
                ...prev,
                aceOpportunityId: {
                    value,
                    error
                }
            }));
            return !error;
        },
        allowedEmailDomains: (e) => {
            const value = e.detail.value;
            const error = validateAllowedEmailDomains(value);
            setFormData(prev => ({
                ...prev,
                allowedEmailDomains: {
                    value,
                    error
                }
            }));
            return !error;
        },
        autoRenewal: (e) => {
            setFormData(prev => ({
                ...prev,
                autoRenewal: e.detail.checked,
            }));
            return true;
        },
        agreementChecked: (e) => {
            setFormData(prev => ({
                ...prev,
                agreementChecked: e.detail.checked
            }));
            return true;
        },
    };
    /**
     * Adds a new row for learning admin data.
     * handleLearningAdministratorListUpdate() will unset error message if it was set before
     * @param e
     */
    const handleAddLearningAdmin = (e) => {
        e.preventDefault();
        formData.learningAdministrators?.push({
            laFirstName: defaultFieldData,
            laLastName: defaultFieldData,
            laEmail: defaultFieldData
        });
        setFormData((prev) => {
            return {
                ...prev,
                learningAdministrators: formData.learningAdministrators
            };
        });
        handleLearningAdministratorListUpdate();
    };
    /**
     * Removes a learning admin row at index
     * handleLearningAdministratorListUpdate() will set an error message if all learning admin rows are removed
     */
    const handleRemoveLearningAdmin = (e, index) => {
        e.preventDefault();
        formData.learningAdministrators?.splice(index, 1);
        setFormData({
            ...formData,
            learningAdministrators: formData.learningAdministrators
        });
        handleLearningAdministratorListUpdate();
    };
    const buildCommonTeamSubscriptionFields = (formData) => {
        const isPartner = sessionStorage.getItem(SessionKey.IS_AMAZON_TRAINING_PARTNER) ? true : false;
        const pricePerSeat = isPartner ? getPartnerPricePerSeat(offerPriceData?.getPayerAccountOfferPrice?.pricePerSeatMap, formData.endCustomerCountry.value) : offerPriceData?.getPayerAccountOfferPrice?.pricePerSeat;
        const geo = isPartner ? getRegionFromCountryName(formData.endCustomerCountry.value) : getRegionFromCountryName(formData.geo.value);
        return {
            firstName: formData.firstName.value.trim(),
            lastName: formData.lastName.value.trim(),
            businessEmail: formData.businessEmail.value.trim(),
            companyName: formData.companyName.value.trim(),
            signInMethod: formData.signInMethod.value.trim(),
            numberOfSeats: +formData.numberOfSeats.value,
            preferredStartDate: formData.preferredStartDate.value
                ? new Date(formData.preferredStartDate.value).toISOString()
                : undefined,
            awsToken: awsJwtToken,
            discountPercentage: discountPercentage ? +discountPercentage : 0,
            linkedAccountId: awsAccountId,
            payerAccountId: offerPriceData?.getPayerAccountOfferPrice?.payerAccountId,
            userId: user.id,
            ...(user?.vibeId && { vibeId: user.vibeId }),
            price: pricePerSeat,
            learningAdministrators: formData.learningAdministrators.map((la) => {
                return {
                    firstName: la.laFirstName.value.trim(),
                    lastName: la.laLastName.value.trim(),
                    email: la.laEmail.value.trim()
                };
            }),
            geo: geo,
            allowedEmailDomains: formData.allowedEmailDomains.value ? formData.allowedEmailDomains.value.split(',').map(domain => domain.trim()).filter(domain => domain).join(',') : undefined,
            language: sessionStorage.getItem('language'),
            ...(!isPartner && { autoRenewal: formData.autoRenewal }),
            ...(!isPartner && { customerCountry: formData.geo.value }),
            ...(isPartner && { distributionSellerName: formData.distributionSellerName.value.trim() })
        };
    };
    const populateSsoFields = (finalizeTeamSubscriptionData) => {
        finalizeTeamSubscriptionData.tcFirstName = formData.tcFirstName.value.trim();
        finalizeTeamSubscriptionData.tcLastName = formData.tcLastName.value.trim();
        finalizeTeamSubscriptionData.tcEmail = formData.tcEmail.value.trim();
    };
    const populatePartnerFields = (finalizeTeamSubscriptionData) => {
        const isPartner = !!sessionStorage.getItem(SessionKey.IS_AMAZON_TRAINING_PARTNER);
        finalizeTeamSubscriptionData.endCustomerCompanyName = formData.endCustomerCompanyName.value.trim();
        finalizeTeamSubscriptionData.endCustomerCountry = formData.endCustomerCountry.value.trim();
        finalizeTeamSubscriptionData.spmsId = isPartner ? 88888888 : undefined; //test value
        finalizeTeamSubscriptionData.partnerRequest = isPartner;
        finalizeTeamSubscriptionData.aceOpportunityId = formData.aceOpportunityId.value.trim();
    };
    /**
     * Called after successful form valiation.
     * The method populates a FinalizeTeamSubscriptionInput object based on customer type and the chosen sign in method
     * @param formData
     * @returns
     */
    const prepareFinalizeTeamSubscriptionData = (formData) => {
        const finalizeTeamSubscriptionData = buildCommonTeamSubscriptionFields(formData);
        if (formData.signInMethod.value === SignInMethod.SSO) {
            populateSsoFields(finalizeTeamSubscriptionData);
        }
        if (isPartnerAccount(awsAccountType)) {
            populatePartnerFields(finalizeTeamSubscriptionData);
        }
        return finalizeTeamSubscriptionData;
    };
    /**
     * Return custom error messages for cases: Max subscription reached, C score low, Invalid email domain
     * otherwise return a generic message
     * @param errorMessageFromServer
     * @returns
     */
    const getFinalizeTeamSubscriptionErrorMessage = (errorMessageFromServer) => {
        const MAX_SUBSCRIPTION_REACHED = 'Customer has maxed out ACTIVE Skill-builder subscriptions'.toLowerCase();
        const C_SCORE_LOW = 'AWS Account Not Authroized'.toLowerCase();
        const INVALID_EMAIL_DOMAIN = 'Invalid email domain'.toLowerCase();
        const AWS_ACCOUNT_NOT_ACTIVE = 'has invalid state'.toLowerCase();
        if (errorMessageFromServer.toLowerCase().includes(MAX_SUBSCRIPTION_REACHED)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | configure_subscription | form_submission | event_failed_of_max_subscriptions`);
            return t('subscription_form_max_subscription_limit_reached');
        }
        else if (errorMessageFromServer.toLowerCase().includes(C_SCORE_LOW)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | configure_subscription | form_submission | event_failed_of_low_c_score`);
            return t('subscription_form_c_score_low', { awsAccountId: awsAccountId });
        }
        else if (errorMessageFromServer.toLowerCase().includes(INVALID_EMAIL_DOMAIN)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | configure_subscription | form_submission | event_failed_of_inavlida_email_domain`);
            return t('subscription_form_invalid_email_domain', { awsAccountId: awsAccountId });
        }
        else if (errorMessageFromServer.toLowerCase().includes(AWS_ACCOUNT_NOT_ACTIVE)) {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | configure_subscription | form_submission | event_failed_of_inactive_aws_account`);
            return t('subscription_form_account_not_active', { awsAccountId: awsAccountId });
        }
        else {
            triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | configure_subscription | form_submission | event_failed`);
            return t('subscription_form_something_went_wrong');
        }
    };
    /**
     * Called on clicking Submit request button.
     * It invokes form valiation. On successful validation, it prepares finalize team subscription input data.
     * Redirect the user to Subscription Management page and displays a success or error notification message at the top
     * @param e
     */
    const handleTeamSubscriptionSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            const finalizeTeamSubscriptionData = prepareFinalizeTeamSubscriptionData(formData);
            finalizeTeamSubscription({
                onCompleted: () => {
                    sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_ID);
                    sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_JWT);
                    let notificationText = '';
                    const isPartner = awsAccountType === AwsAccountType.PARTNER;
                    const isSSO = formData.signInMethod.value === SignInMethod.SSO;
                    if (isSSO) {
                        notificationText = t("subscription_form_submit_success_notification_text_sso_SLA");
                    }
                    else {
                        notificationText = t("subscription_form_submit_success_notification_text_builder_id_SLA");
                    }
                    navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: notificationText, type: 'success' } } });
                    if (isPartner) {
                        triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | configure_subscription | partner_form_submission | event_succeeded`);
                    }
                    else {
                        triggerAnalyticsEvent('toolComplete', `SBTS_${(getNodeEnvironment())} | configure_subscription | regular_form_submission | event_succeeded`);
                    }
                },
                onError: (error) => {
                    console.error(error);
                    setFormSubmitError(error);
                    props.setNotification({ text: getFinalizeTeamSubscriptionErrorMessage(error.message), type: 'error' });
                },
                variables: { input: finalizeTeamSubscriptionData }
            });
        }
        else {
            console.error(`Form validation failed`);
        }
    };
    if (offerPriceDataError?.message) {
        sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_ID);
        sessionStorage.removeItem(SessionKey.AWS_ACCOUNT_JWT);
        navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT, { state: { notification: { text: getFinalizeTeamSubscriptionErrorMessage(offerPriceDataError?.message), type: 'error' } } });
        return _jsx(LoadingContent, { text: "Loading" });
    }
    // Display loading state while reading awsAccountId and/or getting offer price data from backend
    if (!awsAccountId || offerPriceDataLoading || !offerPriceData || !isJwtValid) {
        return _jsx(LoadingContent, { text: "Loading" });
    }
    return (_jsxs(_Fragment, { children: [_jsx(SBTSBreadcrumbGroup, { items: [
                    { text: t('breadcrumb_subscription_management_page'), href: "/" },
                    {
                        text: t('breadcrumb_subscription_order_page'),
                        href: "#"
                    }
                ] }), _jsx("form", { onSubmit: (e) => {
                    handleTeamSubscriptionSubmit(e);
                    if (awsAccountType === AwsAccountType.PARTNER) {
                        triggerAnalyticsEvent('click', `SBTS_${(getNodeEnvironment())} | configure_subscription | partner_form_submission | event_submitted`);
                    }
                    else {
                        triggerAnalyticsEvent('click', `SBTS_${(getNodeEnvironment())} | configure_subscription | regular_form_submission | event_submitted`);
                    }
                }, "aria-label": "form", children: _jsxs(SpaceBetween, { size: "l", children: [_jsxs(ColumnLayout, { columns: 2, children: [_jsx(Box, { variant: "h1", children: t("subscription_form_header") }), _jsx(Box, { float: "right", children: _jsxs(SpaceBetween, { size: "xs", direction: "horizontal", children: [_jsx(Button, { variant: "normal", onClick: e => {
                                                    navigate(ROUTE_SKILLBUILDER_SUBSCRIPTION_MANAGEMENT);
                                                    triggerAnalyticsEvent('click', `SBTS_${(getNodeEnvironment())} | configure_subscription | form_submission | event_cancelled`);
                                                }, children: t("cancel_text") }), _jsx(Button, { variant: "primary", disabled: finalizeTeamSubscriptionLoading, loading: finalizeTeamSubscriptionLoading, children: t("subscription_form_submit_button_text") })] }) })] }), _jsx(Form, { children: _jsxs(Grid, { gridDefinition: [{ colspan: 7 }, { colspan: 5 }], children: [_jsx(Box, { children: _jsxs(SpaceBetween, { size: "l", children: [_jsx(BillingDetailsFormContainer, { formData: formData, onFormDataChange: handleFormDataChange, awsAccountType: awsAccountType }), _jsx(SubscriptionDetailsFormContainer, { onAddLearningAdmin: handleAddLearningAdmin, onRemoveLearningAdmin: handleRemoveLearningAdmin, awsAccountType: awsAccountType, offerPriceData: offerPriceData, formData: formData, onFormDataChange: handleFormDataChange }), _jsx(SubscriptionPaymentDetailsContainer, { awsAccountId: offerPriceData?.getPayerAccountOfferPrice?.payerAccountId })] }) }), _jsx(Box, { children: _jsx(SubscriptionSummaryContainer, { awsAccountId: awsAccountId, numberOfSeats: formData.numberOfSeats.value, offerPriceData: offerPriceData, awsAccountType: awsAccountType, setDiscount: setDiscountPercentage, formData: formData, onFormDataChange: handleFormDataChange, isFTv2Account: isFTv2Account }) })] }) }), _jsx(Box, {})] }) })] }));
};
export const ConfigureSubscriptionPageLayout = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [notification, setNotification] = useState();
    useEffect(() => {
        if (notification) {
            setItems([
                {
                    type: notification?.type,
                    dismissible: true,
                    content: notification?.text,
                    onDismiss: () => {
                        setItems([]);
                    },
                }
            ]);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [notification]);
    return (_jsx(AppLayout, { content: _jsx(ConfigureSubscriptionPage, { setNotification: setNotification }), toolsHide: true, navigationHide: true, notifications: _jsx(Flashbar, { items: items }) }));
};
